import { useCallback, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { canManageScheduleMasterData } from "@/features/auth/permissions";
import { formatRelativeTime } from "@/lib/format-relative-time";
import {
  useRefreshSchedule,
  useSchedule,
  useScheduleEvents,
} from "../api/use-schedules";
import { useLatestRun, useRun, useRunCompletionSync } from "../api/use-runs";
import { useSections } from "../api/use-sections";
import { useScheduleIssues } from "../api/use-issues";
import { CalendarGrid } from "../components/calendar-grid";
import { CalendarFilterBar } from "../components/calendar-filter-bar";
import { CourseRequestsToolbarButton } from "../components/course-requests-toolbar-button";
import { EditRoomsDialog } from "../components/edit-rooms-dialog";
import { EditSubjectAreasDialog } from "../components/edit-subject-areas-dialog";
import { EventDetailPanel } from "../components/event-detail-panel";
import { ExportModal } from "../components/export-modal";
import { ExportToolbarButton } from "../components/export-toolbar-button";
import { SolverInputExportButton } from "../components/solver-input-export-button";
import { ReassignModal } from "../components/reassign-modal";
import type { ReassignHandler } from "../components/reassign-handler";
import type { CourseFocusHandler } from "../components/course-focus-handler";
import { normalizeCourseId } from "../lib/normalize-course-id";
import {
  applyCalendarFilters,
  buildStudentEventLookup,
  buildGradeEventLookup,
  deriveGradeOptions,
  deriveStudentOptions,
} from "../lib/calendar-filters";
import { useCourseRequests } from "../api/use-course-requests";
import { useCalendarFilters } from "../lib/use-calendar-filters";
import { sumIssueUnits } from "../lib/group-issues";
import { RoomsToolbarButton } from "../components/rooms-toolbar-button";
import { RunSchedulerButton } from "../components/run-scheduler-button";
import { SectionRosterPanel } from "../components/section-roster-panel";
import { RunsTab } from "../components/runs-tab";
import { SectionsTab } from "../components/sections-tab";
import { SubjectsToolbarButton } from "../components/subjects-toolbar-button";
import { UploadCourseRequestsDialog } from "../components/upload-course-requests-dialog";
import { ValidationIssuesTab } from "../components/validation-issues-tab";
import {
  formatWeekLabel,
  getCurrentWeekStart,
  getWeekDays,
  zonedDateToUtc,
} from "../components/calendar-grid-helpers";
import { CalendarToolbar } from "../components/calendar-toolbar";
import { SchedulePageHeader } from "../components/schedule-page-header";
import {
  ScheduleTabs,
  isScheduleTab,
  type ScheduleTab,
} from "../components/schedule-tabs";
import type { CalendarEvent, CalendarViewMode, Section } from "../types";

/**
 * Discriminated union describing which (if any) side panel is open. Holding
 * the panel state at the page level keeps focus restoration sane — the
 * `SidePanel` primitive restores focus to whatever element was active before
 * the panel mounted, which is typically the calendar tile / sections row.
 *
 * Slice 4 will introduce a parallel `reassignContext` state for the Reassign
 * modal; that modal is still mounted at this level so a single trigger can
 * close the panel and open the modal without two siblings fighting for focus.
 */
type PanelContext =
  | { type: "event"; event: CalendarEvent }
  | { type: "section"; section: Section };

/**
 * Snapshot of the (student, course, current-section) tuple that drives the
 * Reassign modal. Every call site (calendar tile roster, sections roster,
 * validation issue row) also forwards the friendly `studentName` /
 * `courseName` when known so the modal can render "Name (ID)"; they are null
 * when the source row has no name on file.
 */
interface ReassignContext {
  studentIdentifier: string;
  courseId: string;
  currentSectionLabel: string | null;
  studentName: string | null;
  courseName: string | null;
}

/**
 * Parses a `?tab=` query param and narrows it to the {@link ScheduleTab}
 * union via the {@link isScheduleTab} predicate exported by `schedule-tabs`.
 * Anything outside the known set (or missing entirely) falls back to
 * `calendar` so a typo in the URL never strands the user on an empty page.
 */
function parseTabParam(raw: string | null): ScheduleTab {
  return raw && isScheduleTab(raw) ? raw : "calendar";
}

export function ScheduleHomePage() {
  const { id = "" } = useParams<{ id: string }>();
  const scheduleQuery = useSchedule(id);
  const refreshMutation = useRefreshSchedule();
  const { user } = useAuth();
  const canEditMasterData = canManageScheduleMasterData(user?.role);
  const [roomsOpen, setRoomsOpen] = useState(false);
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [courseRequestsOpen, setCourseRequestsOpen] = useState(false);
  // Phase 6 export modal — opened from the admin-gated header button. The
  // modal mounts unconditionally when the user clicks Export so a screenshot
  // / replay / e2e test can rely on `data-testid="export-modal"` being in the
  // tree once the trigger fires.
  const [exportOpen, setExportOpen] = useState(false);
  // Side-panel state — either an event drawer (calendar click) or a section
  // roster drawer (sections-tab click), never both. Reassign triggers close
  // this panel before opening the modal so the two surfaces don't visually
  // stack.
  const [panelContext, setPanelContext] = useState<PanelContext | null>(null);
  // Reassign modal context — set when the user clicks "Reassign" on a roster
  // row in either side panel OR on a validation-issues row that carries
  // student+course context. Mounted at the page level so a single instance
  // services every trigger.
  const [reassignContext, setReassignContext] =
    useState<ReassignContext | null>(null);
  // Course-focus state — set when the user clicks a course reference in the
  // issues tab. Drives the calendar highlight ring. The `nonce` (bumped via a
  // ref counter) re-fires the grid's scroll effect even on a repeat click of
  // the same course, where `courseId` alone wouldn't change.
  const [focusedCourse, setFocusedCourse] = useState<{
    courseId: string;
    nonce: number;
  } | null>(null);
  const focusNonceRef = useRef(0);
  const latestRun = useLatestRun(id);

  // When a run finishes (started here or picked up from a refresh mid-solve),
  // refresh the caches its results feed: issues, sections, and schedule counts.
  useRunCompletionSync(id);

  /**
   * Common entry point for "Reassign" actions. Closes whichever side panel
   * is open before opening the modal (so we don't visually stack the two
   * surfaces) and aborts with a toast if there's no latest run available —
   * the override endpoint targets a specific run, so we can't proceed
   * without one.
   */
  const handleReassign = useCallback<ReassignHandler>(
    (
      studentIdentifier,
      courseId,
      sectionLabel,
      studentName = null,
      courseName = null,
    ) => {
      if (!latestRun.data) {
        toast.error("No run available. Run the scheduler first.");
        return;
      }
      setPanelContext(null);
      setReassignContext({
        studentIdentifier,
        courseId,
        currentSectionLabel: sectionLabel,
        studentName: studentName ?? null,
        courseName: courseName ?? null,
      });
    },
    [latestRun.data],
  );

  const [weekStart, setWeekStart] = useState<Date>(() =>
    getCurrentWeekStart(new Date()),
  );
  const [viewMode, setViewMode] = useState<CalendarViewMode>("week");
  const [selectedDay, setSelectedDay] = useState<Date>(() => weekStart);

  // Tab state lives in `?tab=` so deep links (e.g. the `RunSchedulerButton`
  // post-success redirect to `?tab=issues`) and refreshes preserve the view.
  // `replace: true` avoids piling up history entries when the user clicks
  // between tabs in a single sitting.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTabParam(searchParams.get("tab"));
  // useCallback so the function identity is stable across renders — without
  // it, the child <ScheduleTabs> would re-render on every parent render even
  // when `?tab=` hasn't changed. Use the updater form of setSearchParams so
  // any concurrent ?param changes are preserved.
  const setActiveTab = useCallback(
    (next: ScheduleTab) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", next);
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // The override carries the `lastImportedAt` snapshot it was set against so a
  // subsequent refresh — which advances `lastImportedAt` — can invalidate it
  // and let the header fall back to the derived "Synced N minutes ago" text.
  // Using a paired state (instead of `useEffect(setState)`) means the override
  // self-clears during render without a cascading re-render.
  const [syncedOverride, setSyncedOverride] = useState<{
    text: string;
    /** `lastImportedAt` at the moment the override was set; `null` if pre-sync. */
    setAt: string | null;
  } | null>(null);

  const scheduleTimeZone = scheduleQuery.data?.timeZone;
  const timeZone = scheduleTimeZone ?? "UTC";

  const lastImportedAt = scheduleQuery.data?.lastImportedAt ?? null;
  // Discard stale overrides whose anchor `lastImportedAt` has been superseded
  // by a successful refresh.
  const activeOverride =
    syncedOverride && syncedOverride.setAt === lastImportedAt
      ? syncedOverride.text
      : null;

  // Derive UTC range covering the visible week in the schedule's timezone.
  // Range is `weekStart` 00:00 (inclusive) to `weekStart + 5 days` 00:00
  // (exclusive) — i.e. Mon..Fri inclusive.
  const { fromUtc, toUtc } = useMemo(() => {
    const fromDate = zonedDateToUtc(
      weekStart.getFullYear(),
      weekStart.getMonth() + 1,
      weekStart.getDate(),
      timeZone,
    );
    const friday = new Date(weekStart);
    friday.setDate(friday.getDate() + 5);
    const toDate = zonedDateToUtc(
      friday.getFullYear(),
      friday.getMonth() + 1,
      friday.getDate(),
      timeZone,
    );
    return { fromUtc: fromDate.toISOString(), toUtc: toDate.toISOString() };
  }, [weekStart, timeZone]);

  const eventsQuery = useScheduleEvents(id, fromUtc, toUtc, {
    enabled: !!scheduleTimeZone,
  });

  // Calendar review filters (client-side, review-only — never touches export).
  // Options derive from the loaded window; filtering applies upstream of
  // CalendarGrid so lane assignment reflects only the visible events. The
  // student facet needs the run's assignments (events carry no roster), so we
  // fetch the full latest run and reset the student pick when the run changes.
  const runDetail = useRun(id, latestRun.data?.id ?? "");
  const assignments = useMemo(
    () => runDetail.data?.assignments ?? [],
    [runDetail.data],
  );
  const {
    filters: calendarFilters,
    toggleValue: toggleFilter,
    removeValue: removeFilter,
    clearAll: clearFilters,
  } = useCalendarFilters(id, latestRun.data?.id);
  const allEvents = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);
  const studentLookup = useMemo(
    () => buildStudentEventLookup(assignments),
    [assignments],
  );
  const studentOptions = useMemo(
    () => deriveStudentOptions(assignments),
    [assignments],
  );

  // Grade facet: grade lives on the course requests (a per-student attribute),
  // so we load them and build a `normalizedStudentIdentifier → grade` map. Rows
  // with a null grade are skipped so the map only holds real grades. Recomputes
  // whenever the course-requests query data changes (e.g. after a re-import).
  const courseRequestsQuery = useCourseRequests(id);
  const gradeById = useMemo(() => {
    const map = new Map<string, number>();
    for (const cr of courseRequestsQuery.data ?? []) {
      if (cr.grade === null) continue;
      map.set(cr.normalizedStudentIdentifier, cr.grade);
    }
    return map;
  }, [courseRequestsQuery.data]);
  const gradeLookup = useMemo(
    () => buildGradeEventLookup(assignments, gradeById),
    [assignments, gradeById],
  );
  const gradeOptions = useMemo(
    () => deriveGradeOptions(assignments, gradeById),
    [assignments, gradeById],
  );

  // A persisted grade with no placed student in the current run has no option;
  // applying it would hide every event with no visible chip to undo it. Prune
  // such stale grades from the *effective* filters so the grid and the chip bar
  // agree. Pruning is DISPLAY-ONLY: it shapes `effectiveFilters` (what the grid
  // filters by and the chip bar renders), while the underlying `calendarFilters`
  // in the store/localStorage is left untouched — so a stale grade intentionally
  // revives if that grade reappears in a later run. Toggle/remove mutations below
  // act on `calendarFilters` (the store), never on this pruned view.
  const effectiveFilters = useMemo(() => {
    if (calendarFilters.grades.length === 0) return calendarFilters;
    const live = calendarFilters.grades.filter((g) =>
      gradeOptions.some((o) => o.value === g),
    );
    if (live.length === calendarFilters.grades.length) return calendarFilters;
    return { ...calendarFilters, grades: live };
  }, [calendarFilters, gradeOptions]);

  const filteredEvents = useMemo(
    () =>
      applyCalendarFilters(
        allEvents,
        effectiveFilters,
        studentLookup,
        gradeLookup,
      ),
    [allEvents, effectiveFilters, studentLookup, gradeLookup],
  );

  // Tab badges. Sections / issues badges only render once their query
  // resolves, so the tab bar doesn't flicker a "0" while data is in flight.
  // Pre-resolved zeroes are intentionally suppressed — the tab itself already
  // carries the label, and a "Sections 0" badge would read as broken.
  const sectionsQuery = useSections(id);
  const issuesQuery = useScheduleIssues(id);

  // Friendly course titles for the Course filter, keyed by normalized code.
  // Sourced from sections (unbounded, so labels resolve even for a picked course
  // that isn't in the current week's window); the filter value stays the code.
  const courseNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const s of sectionsQuery.data ?? []) {
      if (!s.courseName) continue;
      for (const code of s.acceptedNormalizedCourseIds) {
        if (!names.has(code)) names.set(code, s.courseName);
      }
    }
    return names;
  }, [sectionsQuery.data]);
  const sectionCountBadge =
    sectionsQuery.data && sectionsQuery.data.length > 0 ? (
      <span
        data-testid="schedule-tab-sections-count"
        className="bg-page border border-border-subtle rounded-full px-2 py-0.5 text-[11px] font-semibold text-text-soft"
      >
        {sectionsQuery.data.length}
      </span>
    ) : undefined;
  // Student-weighted (sumIssueUnits) so the tab badge matches the banner and
  // the Run button chips rather than the raw row count.
  const issueUnitTotal = issuesQuery.data
    ? sumIssueUnits(issuesQuery.data)
    : 0;
  const issueCountBadge =
    issueUnitTotal > 0 ? (
      <span
        data-testid="schedule-tab-issues-count"
        className="bg-page border border-border-subtle rounded-full px-2 py-0.5 text-[11px] font-semibold text-text-soft"
      >
        {issueUnitTotal}
      </span>
    ) : undefined;

  /**
   * Jump to a course on the calendar and highlight it. Triggered by course
   * references in the issues tab. Locates the target section from the
   * already-loaded (unbounded) sections list — matching the course code, and
   * preferring the section whose `sectionLabel` matches the issue when known —
   * then repositions the calendar to the week/day that section meets before
   * switching to the calendar tab. The events for that week load on arrival,
   * and the grid rings every event serving the course.
   *
   * When no matching section carries timing (e.g. a "no sections exist" issue),
   * it still switches to the calendar tab and sets the highlight — nothing
   * lights up, which is the correct signal that the course has no events.
   */
  const handleCourseFocus = useCallback<CourseFocusHandler>(
    (courseId, sectionLabel = null) => {
      // Clear active review filters first — otherwise a filter that hides the
      // focused course's events would leave the grid's scroll-into-view with no
      // target (silent no-op / dead end for the user).
      clearFilters();
      const normalized = normalizeCourseId(courseId);
      const matching = (sectionsQuery.data ?? []).filter((s) =>
        s.acceptedNormalizedCourseIds.includes(normalized),
      );
      const labelled =
        sectionLabel != null
          ? matching.find((s) => s.sectionLabel === sectionLabel)
          : undefined;
      // Fall back to the earliest-meeting section when the issue's specific
      // section isn't found (or wasn't provided).
      const earliest = matching
        .filter((s) => s.earliestStartUtc)
        .sort((a, b) =>
          (a.earliestStartUtc ?? "").localeCompare(b.earliestStartUtc ?? ""),
        )[0];
      const target = labelled?.earliestStartUtc ? labelled : earliest;
      if (target?.earliestStartUtc) {
        const start = new Date(target.earliestStartUtc);
        setWeekStart(getCurrentWeekStart(start));
        setSelectedDay(start);
      }
      setActiveTab("calendar");
      focusNonceRef.current += 1;
      setFocusedCourse({ courseId, nonce: focusNonceRef.current });
      // Also pop open the section roster side panel for the focused section so
      // the user lands on its details (roster, capacity, reassign), not just a
      // highlighted grid. Prefer the issue's specific section, else the
      // earliest-meeting one, else any matching section — so a real-but-untimed
      // section still opens; only a course with NO section at all (e.g. a "no
      // sections exist" issue) suppresses the panel and closes any open one.
      //
      // Focus note: the course-focus trigger lives in the issues tab, which
      // unmounts on the tab switch above (as it did before this panel existed),
      // so focus can't return to it. The panel's own modal focus is the anchor
      // while open; closing lands focus on the document, matching the prior
      // highlight-only behavior.
      const panelSection = labelled ?? earliest ?? matching[0];
      setPanelContext(
        panelSection ? { type: "section", section: panelSection } : null,
      );
    },
    [sectionsQuery.data, setActiveTab, clearFilters],
  );

  const weekDays = getWeekDays(weekStart);
  const weekLabel = formatWeekLabel(weekDays[0], weekDays[4]);

  const baseSyncedText = scheduleQuery.data
    ? `Synced ${formatRelativeTime(scheduleQuery.data.lastImportedAt)}`
    : "";
  const syncedText = activeOverride ?? baseSyncedText;

  const handleRefresh = async () => {
    if (!id) return;
    // Anchor the "Syncing…" override against the *current* lastImportedAt; it
    // will be replaced as soon as the refresh response lands and the query
    // cache advances.
    const anchorBefore = lastImportedAt;
    setSyncedOverride({ text: "Syncing…", setAt: anchorBefore });
    try {
      const refreshed = await refreshMutation.mutateAsync(id);
      // Anchor against the new `lastImportedAt` so the next successful refresh
      // (or a stale-time refetch that bumps it again) clears this label too.
      setSyncedOverride({
        text: "Synced just now",
        setAt: refreshed.lastImportedAt,
      });
    } catch (err) {
      setSyncedOverride(null);
      const message =
        err instanceof Error ? err.message : "Failed to refresh calendar";
      toast.error(message);
    }
  };

  const handleViewModeChange = (mode: CalendarViewMode) => {
    setViewMode(mode);
    if (mode === "day") {
      // Default to the first day of the visible week if no selection yet.
      const inWeek = weekDays.some(
        (d) =>
          d.getFullYear() === selectedDay.getFullYear() &&
          d.getMonth() === selectedDay.getMonth() &&
          d.getDate() === selectedDay.getDate(),
      );
      if (!inWeek) setSelectedDay(weekDays[0]);
    }
  };

  const shiftWeek = (delta: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + delta * 7);
    setWeekStart(next);
    // Keep selectedDay sane when navigating in day mode.
    const shiftedDay = new Date(selectedDay);
    shiftedDay.setDate(shiftedDay.getDate() + delta * 7);
    setSelectedDay(shiftedDay);
  };

  if (scheduleQuery.isLoading) {
    return (
      <div
        data-testid="schedule-home-loading"
        className="py-16 flex justify-center"
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (scheduleQuery.isError || !scheduleQuery.data) {
    return (
      <div
        data-testid="schedule-home-error"
        className="bg-surface border border-red-500/30 rounded-lg p-6 text-center mx-6 mt-6"
      >
        <p className="text-sm text-red-400">
          {scheduleQuery.error instanceof Error
            ? scheduleQuery.error.message
            : "Failed to load schedule."}
        </p>
      </div>
    );
  }

  const schedule = scheduleQuery.data;

  return (
    <div data-testid="schedule-home-page" className="animate-fade-in -m-6">
      <SchedulePageHeader
        schedule={schedule}
        syncedText={syncedText}
        onRefresh={handleRefresh}
        isRefreshing={refreshMutation.isPending}
        toolbarSlot={
          canEditMasterData ? (
            <>
              <RoomsToolbarButton onClick={() => setRoomsOpen(true)} />
              <SubjectsToolbarButton onClick={() => setSubjectsOpen(true)} />
              <CourseRequestsToolbarButton
                onClick={() => setCourseRequestsOpen(true)}
              />
            </>
          ) : null
        }
        runSlot={<RunSchedulerButton scheduleId={id} />}
      />
      {canEditMasterData && (
        <>
          <EditRoomsDialog
            open={roomsOpen}
            onClose={() => setRoomsOpen(false)}
            scheduleId={id}
          />
          <EditSubjectAreasDialog
            open={subjectsOpen}
            onClose={() => setSubjectsOpen(false)}
            scheduleId={id}
          />
          <UploadCourseRequestsDialog
            isOpen={courseRequestsOpen}
            onClose={() => setCourseRequestsOpen(false)}
            scheduleId={id}
          />
        </>
      )}
      <div className="px-7 py-6">
        <div className="flex items-start justify-between gap-3">
          <ScheduleTabs
            active={activeTab}
            onChange={setActiveTab}
            badges={{
              sections: sectionCountBadge,
              issues: issueCountBadge,
            }}
          />
          {canEditMasterData && (
            <div className="flex items-center gap-2">
              <SolverInputExportButton scheduleId={id} />
              <ExportToolbarButton
                onClick={() => setExportOpen(true)}
                disabled={!latestRun.data}
                disabledReason="Run the scheduler before exporting."
              />
            </div>
          )}
        </div>

        {activeTab === "calendar" && (
          <div data-testid="schedule-tab-calendar-content">
            <CalendarToolbar
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              weekStart={weekStart}
              onPrev={() => shiftWeek(-1)}
              onNext={() => shiftWeek(1)}
              weekLabel={weekLabel}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
            />

            {eventsQuery.isLoading && (
              <div
                data-testid="schedule-events-loading"
                className="py-16 flex justify-center"
              >
                <LoadingSpinner size="lg" />
              </div>
            )}

            {eventsQuery.isError && (
              <div
                data-testid="schedule-events-error"
                className="bg-surface border border-red-500/30 rounded-lg p-4 text-sm text-red-400"
              >
                {eventsQuery.error instanceof Error
                  ? eventsQuery.error.message
                  : "Failed to load events."}
              </div>
            )}

            {!eventsQuery.isLoading && !eventsQuery.isError && (
              <>
                <CalendarFilterBar
                  events={allEvents}
                  filters={effectiveFilters}
                  onToggle={toggleFilter}
                  onRemove={removeFilter}
                  onClearAll={clearFilters}
                  totalCount={allEvents.length}
                  visibleCount={filteredEvents.length}
                  studentOptions={studentOptions}
                  studentDisabled={studentOptions.length === 0}
                  studentEmptyHint={
                    latestRun.data
                      ? "No students placed in this run"
                      : "Run the scheduler to filter by student"
                  }
                  gradeOptions={gradeOptions}
                  // Grade is join of placed students (assignments) × graded
                  // roster (course requests). Disabled until BOTH exist, with
                  // distinct hints: "No run yet" (no assignments) vs "No grades
                  // in roster" (a run exists but no graded course requests join
                  // to it, so the option list is empty).
                  gradeDisabled={gradeOptions.length === 0}
                  gradeEmptyHint={
                    assignments.length === 0
                      ? "No run yet"
                      : "No grades in roster"
                  }
                  courseNames={courseNames}
                />
                {filteredEvents.length === 0 && allEvents.length > 0 ? (
                  <div
                    data-testid="calendar-filter-empty"
                    className="py-16 flex flex-col items-center gap-3 text-sm text-text-muted"
                  >
                    <span>No events match the current filters.</span>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded-md border border-border-subtle bg-surface-2/60 px-3 py-1.5 text-xs font-medium text-text-soft hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <CalendarGrid
                    events={filteredEvents}
                    timeZone={timeZone}
                    weekStart={weekStart}
                    viewMode={viewMode}
                    selectedDay={selectedDay}
                    scheduleId={id}
                    onEventClick={(event) =>
                      setPanelContext({ type: "event", event })
                    }
                    focusedCourseId={focusedCourse?.courseId ?? null}
                    focusNonce={focusedCourse?.nonce}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "issues" && (
          <ValidationIssuesTab
            scheduleId={id}
            canRevalidate={canEditMasterData}
            onReassign={handleReassign}
            onCourseFocus={handleCourseFocus}
          />
        )}

        {activeTab === "sections" && (
          <SectionsTab
            scheduleId={id}
            onSectionClick={(section) =>
              setPanelContext({ type: "section", section })
            }
          />
        )}

        {activeTab === "runs" && <RunsTab scheduleId={id} />}
      </div>

      {panelContext?.type === "event" && (
        <EventDetailPanel
          scheduleId={id}
          event={panelContext.event}
          onClose={() => setPanelContext(null)}
          onReassign={handleReassign}
          // When the reassign modal is opening in the same React commit as
          // this panel closes, skip the panel's focus-restore-on-close so it
          // doesn't steal focus from the modal one tick later (see P1-FE-3).
          skipFocusRestore={reassignContext !== null}
        />
      )}
      {panelContext?.type === "section" && (
        <SectionRosterPanel
          scheduleId={id}
          section={panelContext.section}
          onClose={() => setPanelContext(null)}
          onReassign={handleReassign}
          skipFocusRestore={reassignContext !== null}
        />
      )}
      <ReassignModal
        scheduleId={id}
        runId={latestRun.data?.id ?? ""}
        studentIdentifier={reassignContext?.studentIdentifier ?? ""}
        courseId={reassignContext?.courseId ?? ""}
        studentName={reassignContext?.studentName ?? null}
        courseName={reassignContext?.courseName ?? null}
        currentSectionLabel={reassignContext?.currentSectionLabel ?? null}
        isOpen={reassignContext !== null && latestRun.data !== undefined}
        onClose={() => setReassignContext(null)}
      />
      <ExportModal
        scheduleId={id}
        runId={latestRun.data?.id ?? ""}
        isOpen={exportOpen && latestRun.data !== undefined}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
}
